import { useEffect, useRef, useCallback, useState } from 'react';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { properties } from '../properties';
import timerService from '../services/timerService';

// Remove trailing '/api' from backendApi if present, then append '/ws-timer'
// In production, backendApi is '/api', so WS_URL becomes '/ws-timer' (relative to current domain)
// In local dev, backendApi is 'http://localhost:8080/api', so WS_URL becomes 'http://localhost:8080/ws-timer'
const BASE_URL = properties.backendApi.replace(/\/api\/?$/, '');
const WS_URL = `${BASE_URL}/ws-timer`;

/**
 * Custom hook that manages the full STOMP/WebSocket lifecycle for the Pomodoro timer.
 *
 * Key improvements over the original:
 *  - Commands sent while disconnected are queued and flushed the moment the WS reconnects.
 *  - If the WS has not reconnected within REST_FALLBACK_MS, the queued command is sent
 *    via the REST fallback (`POST /api/timer/command`) instead — this fixes the "buttons
 *    do nothing after laptop wakes from sleep" bug.
 *
 * Returns:
 *  - connected: boolean — whether the WS is active
 *  - sendCommand: (command: string) => void  e.g. sendCommand('START')
 */

const REST_FALLBACK_MS = 3000; // wait this long for WS reconnect before falling back to REST

const useTimerWebSocket = (onStateUpdate) => {
  const clientRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const connectedRef = useRef(false); // always in sync with connected, usable in callbacks

  // Pending commands: [{ command, fallbackTimer }]
  // Each entry holds the command string and the timer id for the REST fallback timeout.
  const pendingRef = useRef([]);

  /**
   * Flush all queued commands through the now-live WebSocket.
   * Called immediately after a successful WS reconnect.
   */
  const flushPending = useCallback(() => {
    const queue = pendingRef.current;
    if (!queue.length) return;
    pendingRef.current = [];
    queue.forEach(({ command, fallbackTimer }) => {
      clearTimeout(fallbackTimer);
      if (clientRef.current?.connected) {
        clientRef.current.publish({
          destination: '/app/timer/command',
          body: JSON.stringify({ command }),
        });
      }
    });
  }, []);

  const sendCommand = useCallback((command) => {
    if (connectedRef.current && clientRef.current?.connected) {
      // WS is live — send immediately
      clientRef.current.publish({
        destination: '/app/timer/command',
        body: JSON.stringify({ command }),
      });
    } else {
      // WS is not connected yet (e.g. just woke from sleep).
      // Queue the command and schedule a REST fallback in case WS takes too long.
      console.warn(`WS not connected — queuing command: ${command}`);

      const fallbackTimer = setTimeout(async () => {
        // Remove this entry from the queue since we're handling it now via REST
        pendingRef.current = pendingRef.current.filter((p) => p.command !== command || p.fallbackTimer !== fallbackTimer);
        try {
          console.info(`WS still down after ${REST_FALLBACK_MS}ms — sending '${command}' via REST fallback`);
          const newState = await timerService.sendCommand(command);
          if (onStateUpdate && newState) onStateUpdate(newState);
        } catch (e) {
          console.error(`REST fallback failed for command '${command}':`, e);
        }
      }, REST_FALLBACK_MS);

      pendingRef.current.push({ command, fallbackTimer });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const client = new Client({
      // SockJS factory — no native WebSocket so SockJS handles fallback
      webSocketFactory: () => new SockJS(WS_URL),

      connectHeaders: {
        Authorization: `Bearer ${token}`,
      },

      reconnectDelay: 5000,
      heartbeatIncoming: 20000,
      heartbeatOutgoing: 20000,

      onConnect: () => {
        connectedRef.current = true;
        setConnected(true);

        // Subscribe to personal timer state channel
        client.subscribe('/user/queue/timer', (message) => {
          try {
            const state = JSON.parse(message.body);
            if (onStateUpdate) onStateUpdate(state);
          } catch (e) {
            console.error('Failed to parse timer state:', e);
          }
        });

        // Flush any commands that were queued while the WS was down (e.g. after sleep)
        flushPending();
      },

      onDisconnect: () => {
        connectedRef.current = false;
        setConnected(false);
      },

      onStompError: (frame) => {
        console.error('STOMP error:', frame);
        connectedRef.current = false;
        setConnected(false);
      },
    });

    client.activate();
    clientRef.current = client;

    return () => {
      // Clear any pending fallback timers to avoid state updates on unmount
      pendingRef.current.forEach(({ fallbackTimer }) => clearTimeout(fallbackTimer));
      pendingRef.current = [];
      client.deactivate();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { connected, sendCommand };
};

export default useTimerWebSocket;
